import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/inbound-rental-agreements"
)({
  component: RouteComponent,
});

function RouteComponent() {
  // InboundRentalAgreementViewSet's read_permission ("inventory.stock.view")
  // gates list/retrieve; write_permission ("inventory.rental.manage") gates
  // just the write UI within.
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <InboundRentalAgreementsPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { LocalSearchSelect } from "@/components/reusable-components/local-search-select";
import { VendorSearchSelect } from "@/components/inventory/shared/VendorSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import {
  useFetchInboundRentalAgreements,
  useInboundRentalAgreementMutations,
} from "@/hooks/api/inventory/useInboundRentalAgreements";
import {
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { InboundRentalAgreementTable } from "@/components/inventory/tables/InboundRentalAgreementTable";
import { InboundRentalAgreementFormSheet } from "@/components/inventory/forms/InboundRentalAgreementFormSheet";
import { InboundReceiveModal } from "@/components/inventory/forms/InboundReceiveModal";
import { InboundReturnModal } from "@/components/inventory/forms/InboundReturnModal";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import type {
  InboundRentalAgreement,
  InboundRentalAgreementPayload,
} from "@/types/inventory";

const INBOUND_STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Received", value: "RECEIVED" },
  { label: "Partially Returned", value: "PARTIALLY_RETURNED" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Closed", value: "CLOSED" },
  { label: "Cancelled", value: "CANCELLED" },
];

function InboundRentalAgreementsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.rental.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>();
  const [vendorFilter, setVendorFilter] = useState<string | undefined>();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<InboundRentalAgreement | null>(null);
  const [receiveFor, setReceiveFor] = useState<InboundRentalAgreement | null>(
    null
  );
  const [returnFor, setReturnFor] = useState<InboundRentalAgreement | null>(
    null
  );
  const [cancelFor, setCancelFor] = useState<InboundRentalAgreement | null>(
    null
  );
  const [deleteFor, setDeleteFor] = useState<InboundRentalAgreement | null>(
    null
  );

  const resetFilters = () => {
    setSearchFilter("");
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setVendorFilter(undefined);
    setPage(1);
  };

  const agreementsQuery = useFetchInboundRentalAgreements({
    page_size: pageSize,
    page,
    search: searchFilter || undefined,
    status: statusFilter,
    warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    vendor: vendorFilter ? parseInt(vendorFilter) : undefined,
  });
  const agreements = agreementsQuery.data?.results ?? [];
  const count = agreementsQuery.data?.count ?? 0;

  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];

  const {
    createAgreement,
    updateAgreement,
    receiveAgreement,
    returnAgreement,
    cancelAgreement,
    deleteAgreement,
  } = useInboundRentalAgreementMutations();

  const handleSubmit = async (payload: InboundRentalAgreementPayload) => {
    if (editing) {
      await updateAgreement.mutateAsync({ id: editing.id, payload });
      toast.success("Agreement updated.");
    } else {
      await createAgreement.mutateAsync(payload);
      toast.success("Agreement created.");
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="p-6 pt-0 space-y-4">
        <PageHeader
          title="Inbound Rentals"
          description="Equipment rented in from external vendors. Rented units flow through stock like any other, but can never be sold."
          onToggleFilter={() => setShowFilters((v) => !v)}
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setFormOpen(true);
                }
              : undefined
          }
          addLabel="New Agreement"
          isLoading={agreementsQuery.isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={agreementsQuery.isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by agreement number..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Status"
            options={INBOUND_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
          <LocalSearchSelect
            className="w-fit"
            items={warehouses}
            keyExtractor={(w) => w.id}
            labelExtractor={(w) => w.name}
            watchedValue={warehouseFilter ?? null}
            onSelect={(v) => {
              setWarehouseFilter(v == null ? undefined : String(v));
              setPage(1);
            }}
            placeholder="Warehouse"
            searchPlaceholder="Search warehouses..."
            noItemsMessage="No warehouses found."
          />
          <VendorSearchSelect
            className="w-fit"
            value={vendorFilter ?? null}
            onChange={(v) => {
              setVendorFilter(v == null ? undefined : String(v));
              setPage(1);
            }}
            placeholder="Vendor"
          />
        </FiltersPanel>

        <InboundRentalAgreementTable
          agreements={agreements}
          isLoading={agreementsQuery.isLoading}
          canManage={canManage}
          onNameClick={(a) =>
            navigate({
              to: "/admin/inventory/inbound-rental-agreements/$id",
              params: { id: String(a.id) },
            })
          }
          onView={(a) =>
            navigate({
              to: "/admin/inventory/inbound-rental-agreements/$id",
              params: { id: String(a.id) },
            })
          }
          onEdit={(a) => {
            setEditing(a);
            setFormOpen(true);
          }}
          onReceive={setReceiveFor}
          onPartialReturn={setReturnFor}
          onCancel={setCancelFor}
          onDelete={setDeleteFor}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={agreementsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <InboundRentalAgreementFormSheet
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        editing={editing}
        warehouses={warehouses}
        isLoading={createAgreement.isPending || updateAgreement.isPending}
      />

      <InboundReceiveModal
        isOpen={!!receiveFor}
        onClose={() => setReceiveFor(null)}
        agreement={receiveFor}
        isLoading={receiveAgreement.isPending}
        onSubmit={async (payload) => {
          if (!receiveFor) return;
          await receiveAgreement.mutateAsync({ id: receiveFor.id, payload });
          toast.success("Units received into stock.");
        }}
      />

      <InboundReturnModal
        isOpen={!!returnFor}
        onClose={() => setReturnFor(null)}
        agreement={returnFor}
        isLoading={returnAgreement.isPending}
        onSubmit={async (payload) => {
          if (!returnFor) return;
          await returnAgreement.mutateAsync({ id: returnFor.id, payload });
          toast.success("Units returned to vendor.");
        }}
      />

      <ConfirmDialog
        isOpen={!!cancelFor}
        onClose={() => setCancelFor(null)}
        onConfirm={async () => {
          if (!cancelFor) return;
          await cancelAgreement.mutateAsync({ id: cancelFor.id });
          toast.success("Agreement cancelled.");
          setCancelFor(null);
        }}
        title="Cancel agreement?"
        description={`${cancelFor?.agreement_number ?? ""} will be marked cancelled. Only a draft agreement can be cancelled — return the units instead once they have been received.`}
        confirmLabel="Cancel Agreement"
        isLoading={cancelAgreement.isPending}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        onConfirm={async () => {
          if (!deleteFor) return;
          await deleteAgreement.mutateAsync({ id: deleteFor.id });
          toast.success("Agreement deleted.");
          setDeleteFor(null);
        }}
        itemName={deleteFor?.agreement_number ?? ""}
        isLoading={deleteAgreement.isPending}
      />
    </SidebarInset>
  );
}
